import React, { useEffect, useState } from 'react';

import { translate } from '@portal/services/i18n';
import { getRouteStackPath } from '@portal/config/routes';
import ConfirmationModal from '@portal/components/ConfirmationModal/ConfirmationModal';
import PaymentPixKey from '@portal/components/PaymentPixKey/PaymentPixKey';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import { useDispatch } from 'react-redux';
import {
  DetailsAction,
  PixActions,
  TransactionsActions,
} from '@portal/actions';
import * as MessageService from '~/services/message';
import { encrypt, signData } from '@portal/repositories/securitySdkConfig';

import TransactionConfirmation from '@portal/components/TransactionConfirmation/TransactionConfirmation';
import { isValidMoneyValue, moneyFormat } from '@portal/utils/format';
import GenerateBilletAmount from '@portal/components/GenerateBilletAmount/GenerateBilletAmount';
import useReduxState from '@portal/hooks/useReduxState';
import { DetailType, PixTransferType } from '@portal/models/enumerators';
import { pix } from '@portal/config/app.json';
import { TransactionStatus } from '@portal/enum/approvalsType';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedAlert from '@portal/components/AdvancedAlert/AdvancedAlert';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedSteps from '@portal/components/AdvancedSteps/AdvancedSteps';
import { UNSET_DETAILS } from '@portal/actions/actionTypes';
import { useNavigation } from '@portal/hooks/useNavigation';

enum TabIndexEnum {
  DECODE = '1',
  AMOUNT = '2',
  CONFIRMATION = '3',
}

const PayPix = () => {
  const navigate = useNavigation();
  const dispatch = useDispatch();

  const {
    details: { transaction, type, error },
  } = useReduxState();

  const [tabKey, setTabKey] = useState('1');
  const [qrCode, setQrCode] = useState('');
  const [amount, setAmount] = useState('');
  const [rawAmount, setRawAmount] = useState(0);
  const [isPinModalVisible, setIsPinModalVisible] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(DetailsAction.unsetDetails());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!transaction) return;

    if (type === DetailType.NEW) {
      setTabKey(transaction.amount ? '3' : '2');
      return;
    }

    if (
      transaction.status === TransactionStatus.PENDING_APPROVE &&
      transaction.transactionRequestPendingApprovalId
    ) {
      navigate(
        `/aprovacao/detalhe-aprovacao/${transaction.transactionRequestPendingApprovalId}`
      );
      return;
    }

    if (transaction.id) {
      navigate(`/inicio/detalhes/${transaction.id}`);
    }
  }, [dispatch, navigate, transaction, type]);

  useEffect(() => {
    if (error) {
      const errorKey = error.message.toUpperCase();
      MessageService.error(`PIX.${errorKey}`);

      dispatch(DetailsAction.setDetailsError(null));
    }
  }, [dispatch, error]);

  const handleDecodeSubmit = () => {
    if (!qrCode) {
      return MessageService.error('COMPONENTS.PAY_PIX.ERROR.NO_CODE', {
        domain: 'validation',
      });
    }
    dispatch(PixActions.decodePixQrCode(qrCode.trim()));
  };

  const handleValueEnter = () => {
    if (rawAmount === 0) {
      MessageService.error('INVALID_AMOUNT');
    } else {
      setTabKey('3');
    }
  };

  const handleConfirmation = (pin: string) => {
    setIsPinModalVisible(false);
    if (!transaction?.pixInfo) {
      return;
    }

    const timestamp = new Date().getTime().toString();
    const amount = (transaction.amount || rawAmount) * 100;

    const transactionHash = signData(
      amount,
      transaction.pixInfo.key,
      timestamp
    );

    const encryptedPIN = encrypt(pin);

    let pixTransferType: PixTransferType | undefined = undefined;

    if (pix.sendPixTransferType && transaction.pixInfo.qrCodeType) {
      pixTransferType = PixTransferType[transaction.pixInfo.qrCodeType];
    }

    const pixInfo = {
      ...transaction.pixInfo,
      pixTransferType: pixTransferType,
    };

    dispatch(
      TransactionsActions.pixTransfer(
        pixInfo,
        transaction.amount || rawAmount,
        {
          pin: encryptedPIN,
          transactionHash,
          timestamp,
        }
      )
    );
  };

  const stepOptions = () => {
    const steps = [
      {
        value: TabIndexEnum.DECODE,
        label: translate('COMPONENTS.PAY_PIX.TAB_TITLES.ONE'),
        children: (
          <>
            <PaymentPixKey
              qrCode={qrCode}
              setQrCode={setQrCode}
              placeholder={translate('COMPONENTS.PAYMENT_PIX_PAY.PLACEHOLDER')}
              onContinue={() => handleDecodeSubmit()}
              onClean={() => setQrCode('')}
            />
          </>
        ),
      },
      {
        value: TabIndexEnum.AMOUNT,
        label: translate('COMPONENTS.PAY_PIX.TAB_TITLES.TWO'),
        children: (
          <>
            {!transaction ? (
              <AdvancedAlert
                title={translate(
                  'COMPONENTS.PAY_PIX.TRANSACTION_NOT_FOUND.TITLE'
                )}
              >
                {translate('COMPONENTS.PAY_PIX.TRANSACTION_NOT_FOUND.MESSAGE')}
              </AdvancedAlert>
            ) : (
              <>
                <GenerateBilletAmount
                  setBilletAmount={setAmount}
                  setBilletRawAmount={setRawAmount}
                  billetAmount={amount}
                  onSubmit={handleValueEnter}
                />
                <div className="pay-pix__button">
                  <AdvancedButton
                    text={translate('COMPONENTS.PAY_PIX.BUTTONS.GO_BACK')}
                    variant="text"
                    onClick={() => {
                      setTabKey('1');
                    }}
                  />
                  <AdvancedButton
                    text={translate('COMPONENTS.PAY_PIX.BUTTONS.CONFIRM')}
                    onClick={handleValueEnter}
                  />
                </div>
              </>
            )}
          </>
        ),
      },
      {
        value: TabIndexEnum.CONFIRMATION,
        label: translate('COMPONENTS.PAY_PIX.TAB_TITLES.THREE'),
        children: (
          <>
            {!transaction ? (
              <AdvancedAlert
                title={translate(
                  'COMPONENTS.PAY_PIX.TRANSACTION_NOT_FOUND.TITLE'
                )}
              >
                {translate('COMPONENTS.PAY_PIX.TRANSACTION_NOT_FOUND.MESSAGE')}
              </AdvancedAlert>
            ) : (
              <TransactionConfirmation
                name={transaction?.pixInfo?.name}
                taxIdentifier={transaction?.pixInfo?.taxIdentifier}
                bank={transaction?.pixInfo?.bank}
                pixKey={transaction?.pixInfo?.key}
                amount={
                  rawAmount
                    ? moneyFormat(rawAmount)
                    : moneyFormat(transaction?.amount)
                }
                dueDate={transaction?.pixInfo?.dueDate}
                fine={
                  isValidMoneyValue(transaction?.pixInfo?.value?.fine)
                    ? moneyFormat(transaction?.pixInfo?.value?.fine)
                    : undefined
                }
                discount={
                  isValidMoneyValue(transaction?.pixInfo?.value?.discount)
                    ? moneyFormat(transaction?.pixInfo?.value?.discount)
                    : undefined
                }
                final={
                  isValidMoneyValue(transaction?.pixInfo?.value?.final)
                    ? moneyFormat(transaction?.pixInfo?.value?.final)
                    : undefined
                }
                original={
                  isValidMoneyValue(transaction?.pixInfo?.value?.original)
                    ? moneyFormat(transaction?.pixInfo?.value?.original)
                    : undefined
                }
                fees={
                  isValidMoneyValue(transaction?.pixInfo?.value?.fees)
                    ? moneyFormat(transaction?.pixInfo?.value?.fees)
                    : undefined
                }
                description={transaction?.pixInfo?.description}
              />
            )}
            <div className="pay-pix__button">
              <AdvancedButton
                text={translate(
                  `COMPONENTS.PAY_PIX.BUTTONS.${
                    !transaction ? 'GO_BACK' : 'CONFIRM'
                  }`
                )}
                onClick={() =>
                  !transaction ? setTabKey('1') : setIsPinModalVisible(true)
                }
              />
            </div>
          </>
        ),
      },
    ];

    return (
      <AdvancedSteps
        testId="pay-pix-steps"
        steps={steps}
        currentStep={tabKey}
        onStepChange={(step) => setTabKey(String(step))}
      />
    );
  };

  const handleToInit = () => {
    setTabKey('1');
    setQrCode('');
    setAmount('');
    setRawAmount(0);
    dispatch({
      type: UNSET_DETAILS,
    });
    navigate(getRouteStackPath('PIX', 'PIX'));
  };

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('PAGES.HOME.PAY.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.PIX'),
            url: `${getRouteStackPath('PIX', 'PIX')}`,
          },
          {
            active: false,
            title: translate('COMPONENTS.PAY_PIX.PAGE_TITLE'),
          },
        ]}
      />
      <div className="pay-pix">
        <AdvancedPanelContentHeader
          testId="pay-pix-header"
          title={translate('COMPONENTS.PAY_PIX.PAGE_TITLE')}
          description={translate('COMPONENTS.PAY_PIX.PAGE_DESCRIPTION')}
          goBackAction={() => handleToInit()}
          buttonTitle={translate('PAGES.HOME.PIX.BUTTONS.BACK_PIX')}
        />
        {stepOptions()}
      </div>
      <ConfirmationModal
        visible={isPinModalVisible}
        setVisible={setIsPinModalVisible}
        handleConfirmation={handleConfirmation}
      />
    </>
  );
};

export default PayPix;
